import { Box, Checkbox, FormControl, FormControlLabel, FormHelperText, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Link, Progress } from '@backstage/core-components';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { RemoteDocBlocks } from './RemoteDocBlocks';
import { useRemoteDoc } from './useRemoteDoc';

// -----------------------------------------------------------------------------------
// Paso de documentación del golden path: pinta un Markdown remoto (p. ej. la
// documentación de la IaC que se va a desplegar) y exige confirmar su lectura.
//
// El documento lo sirve el plugin backend `remote-docs`, que lo lee con la GitHub
// App (el usuario no necesita acceso al repo), inlinea las imágenes y separa los
// diagramas mermaid. Solo se admiten URLs de `remoteDocs.allowedUrlPrefixes`.
//
// El valor del campo es la confirmación: `true` al marcar la casilla y `false`
// al desmarcarla (ya no `undefined`). `default: false` en el esquema no es para que
// `rawErrors` llegue al campo (con rjsf 5.23.2 el `required` del paso también baja a
// `errorSchema.infraDocsAck` sin `default`): es para que el mensaje sea el nuestro.
// El `errorMessage` de la propiedad solo intercepta los errores de SU esquema
// (`enum`, `type`, ...), no el `required` del objeto padre — sin `default: false`,
// al no marcar la clave no existiría y fallaría por `required`, mostrando el
// "must have required property 'infraDocsAck'" de AJV en inglés en la lista de
// arriba, pese a tener `errorMessage`. Con `default: false` la clave siempre existe
// y el único fallo posible es el `enum` de la propia propiedad, que sí está cubierto
// por su `errorMessage`. La obligatoriedad la impone el propio esquema:
//
//   - title: Documentación de la infraestructura
//     required: [infraDocsAck]
//     properties:
//       infraDocsAck:
//         type: boolean
//         default: false
//         enum: [true]
//         errorMessage: Debes confirmar que has leído la documentación antes de continuar.
//         title: Qué infraestructura vas a desplegar
//         ui:field: RemoteDocsField
//         ui:options:
//           hideError: true
//           url: https://github.com/<org>/<repo>/blob/<rama>/<fichero>.md
//         ui:backstage:
//           review:
//             show: false
//
// `enum: [true]`, NO `const: true`: `getDefaultFormState` de `@rjsf/utils` calcula
// el valor inicial del campo priorizando `const` sobre `default` (salvo que se pase
// `experimental_defaultFormStateBehavior.constAsDefaults: 'never'`, que el `Stepper`
// de scaffolder-react no pasa) — con `const: true` la casilla arrancaría YA marcada
// pese al `default: false`, y "Next" avanzaría sin que el usuario la tocara. `enum`
// no entra en ese cómputo, así que sí respeta `default: false`.
//
// `errorMessage` (ajv-errors, ya cargado por el `Stepper` de scaffolder-react) hace
// que el propio AJV devuelva nuestro texto en vez de "must be equal to one of the
// allowed values" en la lista de errores de arriba del formulario (`showErrorList:
// "top"`). `ui:options.hideError: true` apaga el `FieldErrorTemplate` de rjsf que si
// no se pintaría bajo el campo con el mismo texto de AJV: `rawErrors` sigue
// llegando aquí igual (SchemaField de `@rjsf/core` lo pasa sin condicionar a
// `hideError`), así que la casilla puede seguir pintando su propio aviso en
// español. Ver `RemoteDocsField.integration.test.tsx` para la prueba con el
// `Stepper` real.
//
// Si la documentación no se puede cargar, se avisa con un enlace al origen pero el
// formulario sigue siendo usable: una caída de GitHub no debe bloquear el GP.
// -----------------------------------------------------------------------------------

interface RemoteDocsFieldUiOptions {
  /** URL (blob de GitHub) del Markdown. Debe estar en remoteDocs.allowedUrlPrefixes. */
  url?: string;
  /** Texto de la casilla de confirmación. */
  acknowledgeLabel?: string;
  /** Mensaje cuando se intenta avanzar sin marcarla. */
  acknowledgeError?: string;
}

const DEFAULT_ACK_LABEL =
  'He leído la documentación y soy consciente de los costes asociados a esta infraestructura';
const DEFAULT_ACK_ERROR =
  'Debes confirmar que has leído la documentación antes de continuar.';

export const RemoteDocsField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<boolean>) => {
  const options = (uiSchema?.['ui:options'] ?? {}) as RemoteDocsFieldUiOptions;
  const { loading, error, value } = useRemoteDoc(options.url);
  const hasError = (rawErrors?.length ?? 0) > 0;

  return (
    <Box width="100%">
      {schema.title && (
        <Typography variant="h5" component="h2" gutterBottom>
          {schema.title}
        </Typography>
      )}

      {!options.url && (
        <Alert severity="error">
          Configuración incompleta de la plantilla: falta ui:options.url.
        </Alert>
      )}
      {loading && <Progress />}
      {error && (
        <Alert severity="warning">
          No se pudo cargar la documentación ({error.message}). Puedes consultarla en{' '}
          <Link to={options.url!}>{options.url}</Link>
        </Alert>
      )}
      {value?.stale && (
        <Alert severity="info">
          GitHub no está respondiendo: se muestra la última versión disponible de la
          documentación ({new Date(value.fetchedAt).toLocaleString('es-ES')}).
        </Alert>
      )}
      {value && <RemoteDocBlocks blocks={value.blocks} />}

      <FormControl required={required} error={hasError} margin="normal" fullWidth>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={formData === true}
              onChange={e => onChange(e.target.checked)}
            />
          }
          label={options.acknowledgeLabel ?? DEFAULT_ACK_LABEL}
        />
        {hasError && (
          <FormHelperText>{options.acknowledgeError ?? DEFAULT_ACK_ERROR}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export const RemoteDocsFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'RemoteDocsField',
    component: RemoteDocsField,
  }),
);
